// Kachel und Listenzeile der Sonstige-Liste (Handoff Sonstige Kontakte §1, Screens 01–03).
import type { CSSProperties, KeyboardEvent, MouseEvent } from 'react';
import { Buildings, CaretRight, EnvelopeSimple, Phone, User } from '@phosphor-icons/react';
import TelefonLink from '../../../../components/ui/TelefonLink';
import { avatarFarbe, kontaktUnterzeile } from '../../../../lib/kontakte/sonstige';
import type { KontaktUnified } from '../../../../lib/kontakte/types';
import { KontaktAvatar, SonstigeChip } from './teile';

export interface EintragProps {
  kontakt: KontaktUnified;
  firma: boolean;
  /** Position in der Liste – für das gestaffelte Einblenden */
  nr: number;
  onOeffnen: () => void;
}

const stopp = (e: MouseEvent) => e.stopPropagation();
const beiTaste = (f: () => void) => (e: KeyboardEvent<HTMLDivElement>) => {
  if (e.target !== e.currentTarget) return;
  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); f(); }
};
/** Inaktive Kontakte gedimmt (Handoff §1); über eine Variable, damit die Einblend-Animation sie respektiert. */
const deckkraft = (aktiv: boolean) => ({ '--sk-deckkraft': aktiv ? 1 : 0.62 }) as CSSProperties;
const kuerzen: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/** E-Mail als mailto; leer gedimmt und nicht klickbar. */
function MailFeld({ email, flach, stil }: { email: string | null; flach?: boolean; stil: CSSProperties }) {
  const klasse = `${flach ? 'sk-feld-flach' : 'sk-feld sk-tipp'}${email ? '' : ' sk-feld-leer'} flex items-center`;
  const inhalt = (
    <>
      <EnvelopeSimple weight="fill" size={14} color="var(--info)" className="shrink-0" />
      <span style={kuerzen}>{email ?? 'Keine E-Mail'}</span>
    </>
  );
  if (!email) return <span aria-disabled className={klasse} style={stil}>{inhalt}</span>;
  return <a href={`mailto:${email}`} onClick={stopp} data-tip="E-Mail – Mailprogramm öffnen" className={klasse} style={stil}>{inhalt}</a>;
}

/** Telefon über TelefonLink (Webex-Anlage bzw. tel:); leer gedimmt. */
function TelFeld({ telefon, flach, stil }: { telefon: string | null; flach?: boolean; stil: CSSProperties }) {
  // In der Listenzeile unter 520 px nur das Hörer-Icon (sk-tel), die Nummer bleibt für Screenreader lesbar.
  const klasse = `${flach ? 'sk-feld-flach sk-tel' : 'sk-feld sk-tipp'}${telefon ? '' : ' sk-feld-leer'} flex items-center`;
  const inhalt = (
    <>
      <Phone weight="fill" size={14} color="var(--ok)" className="shrink-0" />
      <span className="sk-tel-text" style={kuerzen}>{telefon ?? 'Kein Telefon'}</span>
    </>
  );
  if (!telefon) return <span aria-disabled className={klasse} style={stil}>{inhalt}</span>;
  // TelefonLink kennt kein style: Optik auf der Hülle, der Link füllt sie ganz aus.
  const { padding, gap, ...huelle } = stil;
  return (
    <span data-tip="Anrufen – Nummer wählen" className={klasse} style={{ ...huelle, padding: 0 }}>
      <TelefonLink nummer={telefon} stopPropagation className="flex h-full w-full min-w-0 items-center text-inherit no-underline">
        <span className="flex h-full w-full min-w-0 items-center" style={{ padding, gap }}>{inhalt}</span>
      </TelefonLink>
    </span>
  );
}

export function SonstigeKachel({ kontakt: k, firma, nr, onOeffnen }: EintragProps) {
  const f = avatarFarbe(k.quelle_id);
  const unter = kontaktUnterzeile(k);
  const Wasserzeichen = firma ? Buildings : User;
  const feld: CSSProperties = { height: 34, padding: '0 10px', borderRadius: 12, gap: 7, fontSize: 12, fontWeight: 600, minWidth: 0, textDecoration: 'none' };
  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`${k.anzeigename} – Detailseite öffnen`}
      data-tip={`${k.anzeigename} – Detailseite öffnen`}
      onClick={onOeffnen}
      onKeyDown={beiTaste(onOeffnen)}
      className="sk-glas sk-kachel sk-rise relative flex cursor-pointer flex-col"
      style={{
        isolation: 'isolate', overflow: 'hidden', borderRadius: 24, padding: 16, gap: 14,
        ...deckkraft(k.aktiv), animationDelay: `${Math.min(nr, 12) * 35}ms`,
      }}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ zIndex: -1, background: `radial-gradient(110% 80% at 100% 0%, ${f.schein(0.16)}, transparent 60%)` }} />
      <Wasserzeichen aria-hidden weight="duotone" size={120} className="pointer-events-none absolute"
        style={{ right: -18, bottom: -26, zIndex: -1, color: f.farbe, opacity: 0.12, transform: 'rotate(-12deg)' }} />
      <div className="flex min-w-0 items-center" style={{ gap: 12 }}>
        <KontaktAvatar id={k.quelle_id} name={k.anzeigename} groesse={46} />
        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em', ...kuerzen }}>{k.anzeigename}</span>
          <span className="flex min-w-0 items-center" style={{ gap: 7 }}>
            <SonstigeChip />
            {unter && <span style={{ fontSize: 12.5, color: 'var(--ink3)', ...kuerzen }}>{unter}</span>}
          </span>
        </div>
      </div>
      <div className="flex min-w-0" style={{ gap: 6 }}>
        <MailFeld email={k.email} stil={{ ...feld, flex: 1 }} />
        <TelFeld telefon={k.telefon} stil={{ ...feld, flex: '0 1 auto', maxWidth: '48%' }} />
      </div>
    </div>
  );
}

export function SonstigeZeile({ kontakt: k, nr, onOeffnen }: EintragProps) {
  const unter = kontaktUnterzeile(k);
  const feld: CSSProperties = { height: 32, padding: '0 10px', borderRadius: 11, gap: 7, fontSize: 12.5, fontWeight: 600, minWidth: 0, textDecoration: 'none' };
  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`${k.anzeigename} – Detailseite öffnen`}
      data-tip={`${k.anzeigename} – Detailseite öffnen`}
      onClick={onOeffnen}
      onKeyDown={beiTaste(onOeffnen)}
      className="sk-zeile sk-rise flex cursor-pointer items-center"
      style={{ gap: 12, padding: '9px 12px', borderRadius: 20, ...deckkraft(k.aktiv), animationDuration: '.4s', animationDelay: `${Math.min(nr, 12) * 35}ms` }}
    >
      <KontaktAvatar id={k.quelle_id} name={k.anzeigename} groesse={38} />
      <div className="flex min-w-0 flex-col" style={{ flex: '1 1 200px', gap: 1 }}>
        <span style={{ fontSize: 14, fontWeight: 800, ...kuerzen }}>{k.anzeigename}</span>
        <span style={{ fontSize: 12, color: 'var(--ink3)', ...kuerzen }}>{unter ?? '—'}</span>
      </div>
      <span className="sk-breit shrink-0"><SonstigeChip /></span>
      <span className="sk-breit min-w-0" style={{ flex: '0 1 240px' }}>
        <MailFeld email={k.email} flach stil={{ ...feld, flex: 1 }} />
      </span>
      <TelFeld telefon={k.telefon} flach stil={{ ...feld, flex: '0 0 150px', maxWidth: 150 }} />
      <CaretRight aria-hidden weight="bold" size={13} color="var(--ink3)" className="shrink-0" />
    </div>
  );
}
